import { Component, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { isNumber } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import {
  BehaviorSubject,
  catchError,
  delayWhen,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { AppRoutes } from '@app';
import {
  EExpressWaybillPaymentStatus,
  IApiError,
  PaymentCostsInfo,
  PaymentCostsInfoArrayItem,
  Invoice,
  InternationalInvoiceDeclarationInfo,
  User,
  InternationalInvoice,
} from '@models';
import {
  AlertToastService,
  AuthService,
  ClaimService,
  GoogleAnalyticService,
  InternationalInvApiService,
  BreakpointService,
  InvoiceService,
  MasterpassService,
} from '@services';
import {
  checkCanEdit,
  formatCost,
  handleFirstDayStorage,
  handleReturnCargoDate,
  RedirectInvoicesFormComponent,
  RedirectSuccessPopupComponent,
  RedirectSuccessPopupData,
  todayEqualOrGreaterThan,
  Unsubscriber,
  AllowedStatusCodes,
  PayInvoiceType,
  ChangeEwSuccessPopupComponent,
  ShowInvoiceTrackedComponent,
  payerNameTranslationsLong,
  payerNameTranslationsShort,
  paymentMethodTranslations,
  FullscreenMobileModalConfig,
  BannerType,
  roundToPrecision,
  CardTypes,
} from '@shared';
import { MasterpassPaymentComponent } from '../../../components/dashboard/acquiring/components/masterpass-payment/masterpass-payment.component';
import { InvoiceServiceRequest } from '../../../models/invoice/invoice-service-request.model';
import { InvoiceApiService } from '../../../services/invoices/invoice-api.service';
import { TrackDeliveryService } from '../../../services/track-delivery.service';
import { BackInvoicesFormComponent, BackSuccessPopupComponent, BackSuccessPopupData } from '../back-invoices-form';
import { ChangeCustomerReturnComponent } from '../change-customer-return/change-customer-return.component';
import { ChangeEwSuccessPopupData } from '../change-ew/change-ew-success-popup/change-ew-success-popup.interface';
import { ChangeEwComponent } from '../change-ew/change-ew.component';
import { LightReturnService } from '../light-return-modal/light-return.service';
import { InvoiceStateStatus } from '../../../services/invoices/invoice-states.config';
import { DeclarationPaymentComponent } from '../../../components/dashboard/acquiring/components/declaration-payment/declaration-payment.component';
import { CustomsDeclarationInfoTooltip } from './show-invoice-international.constants';

const componentName = 'np-show-invoice-international';

@Component({
  selector: componentName,
  templateUrl: './show-invoice-international.component.html',
  styleUrls: ['./show-invoice-international.component.scss', '../../../../styles/invoice-show.scss'],
})
export class ShowInvoiceInternationalComponent extends Unsubscriber implements OnInit, OnDestroy {
  protected readonly checkCanEdit = checkCanEdit;
  protected readonly ExpressWaybillPaymentStatus = EExpressWaybillPaymentStatus;
  protected readonly BannerType = BannerType;
  protected readonly CustomsDeclarationInfoTooltip = CustomsDeclarationInfoTooltip;
  readonly payerNameTranslationsShort = payerNameTranslationsShort;
  readonly paymentMethodTranslations = paymentMethodTranslations;

  private _pending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  pending$ = this._pending.asObservable();

  private _newInvoicePending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  newInvoicePending$: Observable<boolean> = this._newInvoicePending.asObservable();

  invoice: Invoice | any;
  getStatusDocumentsInvoice: Invoice;
  newInvoice: Invoice;
  redirectRequest: InvoiceServiceRequest;
  returnRequest: InvoiceServiceRequest;
  paymentCostsInfo: PaymentCostsInfo;
  declarationsPaymentCostsInfo: PaymentCostsInfoArrayItem[] = [];
  formattedInvoiceNumber: string;
  trackingDocuments: string[] = [];
  paymentSum: string;
  commissionPayer: string;

  isShowFull: boolean = false;
  isPossibilityDuplicate: boolean = false;
  shouldReload: boolean = false;
  currentStateIsArchived: boolean = false;
  corporate: boolean = false;
  paymentPopupOpened: boolean = false;
  allowManageButton: boolean = false;

  lightReturnPending$ = this.lightReturnService.pending$;
  loading$: Observable<boolean> = merge(this.lightReturnService.pending$, this.pending$);

  isMobile$ = this.breakpointService.isMobile$;
  isDesktop$ = this.breakpointService.isDesktop$;

  closed: EventEmitter<{ success: boolean }> = new EventEmitter<{ success: boolean }>();

  private _closeParams: Record<string, string | number | boolean> = null;

  set closeParams(value: Record<string, string | number | boolean>) {
    this._closeParams = { ...this._closeParams, ...value };
  }

  public get closeParams(): Record<string, string | number | boolean> {
    return this._closeParams;
  }

  @HostBinding('class')
  @Input()
  inputInvoice: Invoice | any;
  @Input() classes = 'modal-flex';
  @Input() type: PayInvoiceType;
  @Input() contactPhone: string;
  @Input() recipientCounterpartyType: string;
  @Input() backMoney: number;
  @Input() paymentControl: number;
  @Input() isInvoiceFromSearch = false;

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.dialog.closeAll();
    }
  }

  set pending(value: boolean) {
    this._pending.next(value);
  }

  set newInvoicePending(value: boolean) {
    this._newInvoicePending.next(value);
  }

  get showAdditionalServices(): boolean {
    if (!this.inputInvoice) {
      return false;
    }

    return (
      (!!this.inputInvoice.backMoney && !!this.inputInvoice.backDeliveryPayer) ||
      !!this.inputInvoice.paymentControl ||
      (this.inputInvoice.RedirectingReturnIndication && !this.redirectRequest && !this.returnRequest)
    );
  }

  get user(): User {
    return this.authService.user;
  }

  get invoiceStateInformerDate(): string {
    if (!this.invoice?.state?.name) {
      return '';
    }

    switch (this.invoice?.state?.name) {
      case InvoiceStateStatus.ArrivedAtBranch:
        return this.invoice.actualDeliveryDate || this.invoice.ArrivalDateTime;
      case InvoiceStateStatus.ShipmentReceived:
        return this.invoice.receivingTime;
      default:
        return '';
    }
  }

  constructor(
    private router: Router,
    private invoiceService: InvoiceService,
    private internationalInvApiService: InternationalInvApiService,
    private invoicesService: InvoiceApiService,
    private alertToastService: AlertToastService,
    private dialogRef: MatDialogRef<ShowInvoiceInternationalComponent>,
    private dialog: MatDialog,
    protected storage: LocalStorageService,
    private authService: AuthService,
    private trackDeliveryService: TrackDeliveryService,
    private lightReturnService: LightReturnService,
    private cs: ClaimService,
    private breakpointService: BreakpointService,
    protected googleAnalyticsService: GoogleAnalyticService,
    protected masterpassService: MasterpassService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.backMoney = this.inputInvoice.backMoney;
    this.paymentControl = this.inputInvoice.paymentControl;
    this.corporate = !!this.user.contacts;
    this.currentStateIsArchived = this.router.url.includes(AppRoutes.invoices_archived);
    this.commissionPayer = payerNameTranslationsLong[this.inputInvoice.backDeliveryPayer] || '';
    this.paymentCalc();
    this.getInvoice();
    this.getDeclarationPaymentCostsInfo();
    this.initTrackingDocuments();
  }

  isPrivatePerson(): boolean {
    return this.user.loyaltyCardType === CardTypes.ROZNIZA;
  }

  hideIsPaid(): boolean {
    return (
      this.inputInvoice?.InternationalDeliveryType &&
      this.inputInvoice.InternationalDeliveryType === 'Export' &&
      this.inputInvoice.PayerType === 'Recipient'
    );
  }

  getDeliveryPrice(invoice: Invoice): string {
    const price = invoice?.deliveryPrice + invoice?.StoragePrice;

    return formatCost(price, invoice?.DocumentCostCurrencyCode, true);
  }

  getAnnouncedPrice(invoice: Invoice): string {
    return formatCost(invoice.Cost, invoice.InvoiceCurrencyCode, true)
  }

  isDeclarationPaid(declarationPaymentInfo: PaymentCostsInfoArrayItem): boolean {
    return declarationPaymentInfo?.InternationalDeliveryPaid === declarationPaymentInfo?.TotalInternationalDelivery;
  }

  getDeclarationCost(cost: number): string {
    return formatCost(cost, this.inputInvoice?.DocumentCostCurrencyCode, true);
  }

  switchShowFull(): void {
    this.isShowFull = !this.isShowFull;

    this.googleAnalyticsService.track('analitics', `${componentName}.switchShowFull`, {
      eventLabel: `Повна інформація`,
      eventCategory: `${componentName}`,
      eventAction: this.isShowFull ? 'open' : 'close',
    });
  }

  redirectInvoice(invoice: Invoice): void {
    const dialogRef = this.dialog.open(RedirectInvoicesFormComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
            panelClass: 'no-padding',
          }),
    });

    const typeOfDocument = invoice.TypeOfDocument === 'Incoming' || this.type === 'in' ? 'in' : 'out';

    dialogRef.componentInstance.type = typeOfDocument;
    dialogRef.componentInstance.inputInvoice = invoice;

    dialogRef
      .afterClosed()
      .pipe(switchMap((value) => this.handleRedirectPopupClose(value, typeOfDocument)))
      .subscribe((data) => {
        if (data?.success) {
          this.dialogRef.close({ success: true });
        }
      });

    this.googleAnalyticsService.track('analitics', `${componentName}.redirectInvoice`, {
      eventLabel: `Заявка на переадресацію`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
    const keys$ = dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape'));
    const backdrop$ = dialogRef.backdropClick();
    this.subscriptions = merge(keys$, backdrop$, dialogRef.afterClosed()).subscribe((data) => {
      if (data?.success) {
        this.closeParams = { success: true };
        this.dialogRef.close();
      }
    });
  }

  copy(invoice: Invoice): void {
    this.invoicesService
      .getForRef(invoice.docNumber)
      .pipe(map((response) => response.data.data))
      .subscribe((data) => {
        data.map((i) => {
          this.router.navigate([AppRoutes.invoice_copy, i.Ref]);
          this.closePopups();
        });
      });

    this.googleAnalyticsService.track('analitics', `${componentName}.copy`, {
      eventLabel: `Дублювати накладну`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
  }

  copyInternational(invoice: InternationalInvoice): void {
    this.pending = true;

    this.internationalInvApiService
      .getInvoice(invoice.InternationalExpressWaybill)
      .pipe(
        finalize(() => (this.pending = false)),
        catchError(() => {
          this.alertToastService.pushError('Не вдалося завантажити дані. Будь ласка, створіть відправлення самостійно');
          return EMPTY;
        }),
      )
      .subscribe((invoice) => {
        if (invoice?.Ref) {
          this.closePopups();
          this.router.navigate([AppRoutes.invoice_copy_international, invoice.Ref]);
        } else {
          this.alertToastService.pushError('Не вдалося завантажити дані. Будь ласка, створіть відправлення самостійно');
        }
      });

    this.googleAnalyticsService.track('analitics', `${componentName}.copy_international`, {
      eventLabel: `Дублювати міжнародну накладну`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
  }

  close(): void {
    this.closeParams = { success: this.shouldReload };
    this.dialogRef.close();
  }

  returnInvoice(invoice: Invoice): void {
    const dialogRef = this.dialog.open(BackInvoicesFormComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
            panelClass: 'no-padding',
          }),
    });
    const typeOfDocument = invoice.TypeOfDocument === 'Incoming' || this.type === 'in' ? 'in' : 'out';
    dialogRef.componentInstance.type = typeOfDocument;
    dialogRef.componentInstance.invoice = invoice;
    dialogRef
      .keydownEvents()
      .pipe(
        take(1),
        filter((e) => e.key === 'Escape'),
      )
      .subscribe((data) => {
        dialogRef.close();
      });
    dialogRef
      .afterClosed()
      .pipe(switchMap((value) => this.handleReturnPopupClose(value, invoice, typeOfDocument)))
      .subscribe((data) => {
        if (data?.success) {
          this.dialogRef.close({ success: true });
        }
      });

    this.googleAnalyticsService.track('analitics', `${componentName}.returnInvoice`, {
      eventLabel: `Заявка на повернення`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
    const keys$ = dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape'));
    const backdrop$ = dialogRef.backdropClick();
    merge(keys$, backdrop$, dialogRef.afterClosed()).subscribe((data) => {
      if (data?.success) {
        this.closeParams = { success: true };
        this.dialogRef.close();
      }
    });
  }

  print(invoice: Invoice): void {
    if (!invoice) {
      return;
    }

    this.googleAnalyticsService.track('analitics', `${componentName}.print`, {
      eventLabel: `Друк експрес-накладної, 2 копiї`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });

    this.invoiceService
      .printCopies([invoice.docNumber], 2)
      .pipe(finalize(() => (this.shouldReload = true)))
      .subscribe({
        next: () => {
          this.googleAnalyticsService.track('analitics', `${componentName}.print.success`, {
            eventLabel: `Друк експрес-накладної, 2 копiї`,
            eventCategory: `${componentName}`,
            eventAction: 'print.success',
          });
        },
        error: (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          this.googleAnalyticsService.track('analitics', `${componentName}.print.error`, {
            eventLabel: `Друк експрес-накладної, 2 копiї: ${errors[0] && errors[0].errorCode}`,
            eventCategory: `${componentName}`,
            eventAction: 'print.error',
          });
        },
      });
  }

  printOneCopy(invoice: Invoice): void {
    if (!invoice) {
      return;
    }

    this.googleAnalyticsService.track('analitics', `${componentName}.printOneCopy`, {
      eventLabel: `Друк експресс-накладної, 1 копія`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });

    this.invoiceService
      .printCopies([invoice.docNumber])
      .pipe(finalize(() => (this.shouldReload = true)))
      .subscribe({
        next: () => {
          this.googleAnalyticsService.track('analitics', `${componentName}.printOneCopy.success`, {
            eventLabel: `Друк експресс-накладної, 1 копія`,
            eventCategory: `${componentName}`,
            eventAction: 'printOneCopy.success',
          });
        },
        error: (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          this.googleAnalyticsService.track('analitics', `${componentName}.printOneCopy.error`, {
            eventLabel: `Друк експресс-накладної, 1 копія: ${errors[0] && errors[0].errorCode}`,
            eventCategory: `${componentName}`,
            eventAction: 'printOneCopy.error',
          });
        },
      });
  }

  changeEw(invoice: Invoice): void {
    if (!invoice) {
      console.warn('Invoice is undefined');
      return;
    }

    this.googleAnalyticsService.track('analitics', `${componentName}.changeEw`, {
      eventLabel: `Заявка на зміну даних`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });

    const dialogRef = this.dialog.open(ChangeEwComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
            panelClass: 'no-padding',
            disableClose: true,
          }),
    });
    dialogRef.componentInstance.type = 'out';
    dialogRef.componentInstance.invoice = invoice;
    dialogRef
      .keydownEvents()
      .pipe(
        take(1),
        filter((e) => e.key === 'Escape'),
      )
      .subscribe(() => {
        dialogRef.close();
      });
    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        switchMap((value) => this.handleChangeEwPopupClose(value)),
      )
      .subscribe((data) => {
        if (data?.success) {
          this.closeParams = { success: true };
          this.dialogRef.close();
        }
        dialogRef.close();
      });
  }

  changeCustomerReturn(invoice: Invoice): void {
    if (!invoice) {
      console.warn('Invoice is undefined');
      return;
    }

    this.googleAnalyticsService.track('analitics', `${componentName}.changeCustomerReturn`, {
      eventLabel: `Заявка на подовження терміну зберігання`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });

    const dialogRef = this.dialog.open(ChangeCustomerReturnComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
            panelClass: 'no-padding',
            disableClose: true,
          }),
    });
    dialogRef.componentInstance.type = this.type;
    dialogRef.componentInstance.invoice = this.inputInvoice;
    dialogRef.componentInstance.user = this.corporate ? null : this.user;

    const keys$ = dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape'));
    const backdrop$ = dialogRef.backdropClick();

    merge(keys$, backdrop$, dialogRef.afterClosed()).subscribe((data) => {
      if (data?.success) {
        this.closeParams = { success: true };
        this.dialogRef.close();
      }
    });
  }

  checkPaymentDisabled(): boolean {
    return (
      this.inputInvoice?.ExpressWaybillPaymentStatus === EExpressWaybillPaymentStatus.PaymentNotAvailable ||
      (this.declarationsPaymentCostsInfo?.some((declaration) => !this.isDeclarationPaid(declaration)) &&
        this.isPrivatePerson())
    );
  }

  showPaymentButton(): boolean {
    return (
      (this.inputInvoice?.ExpressWaybillPaymentStatus === EExpressWaybillPaymentStatus.NeedPayment ||
        this.inputInvoice?.ExpressWaybillPaymentStatus === EExpressWaybillPaymentStatus.PaymentNotAvailable) &&
      !this.isPaid(this.paymentCostsInfo)
    );
  }

  addDocumentToArchive(invoice: Invoice): void {
    const delay = () => timer(2000);
    this.invoicesService
      .addDocumentToArchive(invoice, this.type as 'in' | 'out')
      .pipe(delayWhen(delay))
      .subscribe({
        next: () => {
          this.alertToastService.pushSuccess(`Накладну успiшно заархiвовано`);
          this.closeParams = { success: true };
          this.dialogRef.close();

          this.googleAnalyticsService.track('analitics', `${componentName}.addDocumentToArchive`, {
            eventLabel: `Архівувати`,
            eventCategory: `${componentName}`,
            eventAction: 'click',
          });
        },
        error: (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }

          this.googleAnalyticsService.track('analitics', `${componentName}.addDocumentToArchive.error`, {
            eventLabel: `\`Помилка Заархiвувати: ${invoice.docNumber} ${errors[0] && errors[0].errorCode}`,
            eventCategory: `${componentName}`,
            eventAction: 'addDocumentToArchive.error',
          });
        },
      });
  }

  openDeclarationPayment(declarationPaymentCostsInfo: PaymentCostsInfoArrayItem): void {
    const dialogRef = this.dialog.open(DeclarationPaymentComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
            position: { right: '0' },
            width: '75%',
            height: '100%',
            panelClass: 'setting-content-popup',
          }),
      data: {
        declarationPaymentCostsInfo,
        senderName: this.inputInvoice?.CounterpartySenderDescription || '—',
        recipientPhone: this.contactPhone || this.inputInvoice?.recipient?.phone,
        currencyCode: this.inputInvoice?.DocumentCostCurrencyCode,
      },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data.success) {
        this.getDeclarationPaymentCostsInfo();
      }
    });
  }

  createPayment(invoice: Invoice): void {
    this.paymentPopupOpened = true;
    const dialogRef = this.dialog.open(MasterpassPaymentComponent, {
      ...(this.breakpointService.isMobile
        ? FullscreenMobileModalConfig
        : {
            position: { right: '0' },
            width: '75%',
            height: '100%',
            panelClass: 'setting-content-popup',
          }),
    });
    dialogRef.componentInstance.type = this.type;
    dialogRef.componentInstance.inputInvoice = this.inputInvoice;
    dialogRef.componentInstance.invoiceData = {
      docNumber: this.inputInvoice.docNumber,
      SendersPhone: this.inputInvoice.PhoneSender ?? this.inputInvoice.SendersPhone,
      RecipientsPhone: (this.inputInvoice as any).PhoneRecipient ?? (this.inputInvoice as any).RecipientsPhone,
      Amount: this.inputInvoice.Cost,
    };
    dialogRef.componentInstance.paymentCostsInfo = this.paymentCostsInfo;
    dialogRef.componentInstance.recipientCounterpartyType = invoice.recipientCounterpartyType;

    dialogRef.afterClosed().subscribe(() => {
      this.paymentPopupOpened = false;
      this.getInvoice();
    });

    this.googleAnalyticsService.track('analitics', `${componentName}.createPayment`, {
      eventLabel: `Сплатити`,
      eventCategory: `${componentName}`,
      eventAction: 'click',
    });
  }

  isPaid(paymentCostsInfo: PaymentCostsInfo): boolean {
    if (!paymentCostsInfo) {
      return true;
    }

    return !(
      paymentCostsInfo.Services > 0 ||
      paymentCostsInfo.AfterPayment > 0 ||
      paymentCostsInfo.MoneyTransfer > 0 ||
      paymentCostsInfo.InternationalDelivery > 0 ||
      paymentCostsInfo.Commission > 0 ||
      paymentCostsInfo.Redirecting > 0 ||
      paymentCostsInfo.Return > 0
    );
  }

  goToNewInvoice(docNumber: string, event: MouseEvent): void {
    event.preventDefault();
    this.showNewInvoice(docNumber);
  }

  showNewInvoice(documentNumber: string): void {
    this.pending = true;
    this.newInvoicePending = true;

    this.subscriptions = this.getInvoiceByNumber(documentNumber)
      .pipe(
        finalize(() => {
          this.pending = false;
          this.newInvoicePending = false;
        }),
        switchMap((invoice: Invoice) => {
          this.closePopups();

          const invoiceToOpen = invoice ? invoice : this.newInvoice;
          const documentType = invoice
            ? invoice.TypeOfDocument === 'Incoming' || invoice.TypeOfDocument === 'in'
              ? 'in'
              : 'out'
            : this.type;

          const isTrackedDocument = !invoice;

          const modalConfig = {
            ...(this.breakpointService.isMobile
              ? FullscreenMobileModalConfig
              : {
                  panelClass: 'no-padding',
                }),
            disableClose: false,
          };

          const component = isTrackedDocument ? ShowInvoiceTrackedComponent : ShowInvoiceInternationalComponent;
          const dialogRef = this.dialog.open<ShowInvoiceTrackedComponent | ShowInvoiceInternationalComponent>(
            component,
            modalConfig,
          );

          if (!isTrackedDocument) {
            (dialogRef as MatDialogRef<ShowInvoiceInternationalComponent>).componentInstance.isInvoiceFromSearch = true;
          }

          dialogRef.componentInstance.type = documentType;
          dialogRef.componentInstance.inputInvoice = invoiceToOpen;

          return dialogRef.backdropClick().pipe(map(() => dialogRef));
        }),
      )
      .subscribe({
        next: (dialogRefToClose) => {
          dialogRefToClose.close(this.closeParams);
        },
        error: (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
        },
      });
  }

  closePopups(): void {
    this.dialog.closeAll();
  }

  lightReturn(): void {
    this.lightReturnService.init(this.inputInvoice);
  }

  claim(invoice: Invoice): void {
    this.cs.open(invoice.docNumber);
  }

  private getInvoice(): void {
    this.formattedInvoiceNumber = this.invoiceService.makeFormatedInvoiceNumber(this.inputInvoice.docNumber);
    this.pending = true;
    this.invoiceService
      .track(this.inputInvoice.docNumber, this.inputInvoice?.PhoneSender ?? undefined)
      .pipe(
        switchMap((invoice) => {
          this.getStatusDocumentsInvoice = invoice;

          invoice.announcedPrice = this.inputInvoice?.announcedPrice || this.inputInvoice?.Cost;
          invoice.RecipientFullName = this.inputInvoice?.RecipientFullName;

          this.allowManageButton = AllowedStatusCodes.some((e) => invoice.statusCode === e);

          this.handleInvoiceMutations(this.isInvoiceFromSearch ? this.inputInvoice : invoice);
          this.invoice.CheckWeightMethod = invoice.CheckWeightMethod;
          this.inputInvoice.state = this.invoice.state;

          // @TODO this logic should be revised by business analyst
          if (
            this.isInvoiceFromSearch &&
            ((![7, 8].some((statusCode) => statusCode === Number(invoice.statusCode)) &&
              !(
                invoice &&
                invoice.statusCode &&
                !!invoice.dateFirstDayStorage &&
                this.isTodayGreaterThanFirstDayStorage(invoice)
              )) ||
              [105].some((statusCode) => statusCode === Number(invoice.statusCode)))
          ) {
            this.invoice.state.name = invoice.state.name;
          }

          if (this.invoice.serviceRequests) {
            this.invoice.serviceRequests.forEach((request) => {
              if (request.serviceName === 'Redirecting') {
                this.redirectRequest = request;
              }

              if (request.serviceName === 'CargoReturn') {
                this.returnRequest = request;
              }
            });
          }

          if (this.redirectRequest || this.returnRequest) {
            this.invoiceService
              .track((this.redirectRequest || this.returnRequest).newInvoiceNumber)
              .pipe(take(1))
              .subscribe((trackedInvoice) => {
                this.newInvoice = trackedInvoice;
              });
          }

          return this.masterpassService.getPaymentInfo(
            this.invoice.docNumber,
            this.type === 'in'
              ? this.invoice.recipient && (this.invoice.recipient.phone as string)
              : this.authService.user.phone || (this.invoice.sender ? this.invoice.sender.phone : ''),
          );
        }),
        finalize(() => (this.pending = false)),
        take(1),
      )
      .subscribe((paymentCostsInfo) => {
        this.paymentCostsInfo = paymentCostsInfo;
      });
  }

  private initTrackingDocuments(): void {
    if (this.storage.isStorageAvailable()) {
      const trackingDocumentsArray = this.trackDeliveryService.getTrackingDocuments();
      if (!!trackingDocumentsArray) {
        this.trackingDocuments = trackingDocumentsArray;
      }
    }
  }

  private getDeclarationPaymentCostsInfo(): void {
    const declarations: InternationalInvoiceDeclarationInfo[] = this.inputInvoice.Declarations || [];

    if (declarations.length) {
      this.pending = true;

      this.masterpassService
        .getPaymentInfoArray(declarations.map((d) => d.DeclarationNumber))
        .pipe(
          finalize(() => (this.pending = false)),
          take(1),
        )
        .subscribe({
          next: (paymentCostsInfoArray) => {
            this.declarationsPaymentCostsInfo = paymentCostsInfoArray ?? [];
          },
          error: (errors: IApiError[]) => {
            if (errors[0]) {
              this.alertToastService.pushApiErrors(errors);
            }
          },
        });
    }
  }

  private getInvoiceByNumber(invoiceNumber: string): Observable<Invoice | null> {
    return this.invoicesService.searchInvoices(invoiceNumber, { page: 1, limit: 5 }).pipe(
      finalize(() => (this.pending = false)),
      map(({ invoices, totalCount }) => {
        if (!totalCount) {
          return null;
        }

        return invoices.map((innerInvoices: Invoice[]) => innerInvoices.map(handleFirstDayStorage)[0])[0];
      }),
    );
  }

  private handleChangeEwPopupClose(value: ChangeEwSuccessPopupData): Observable<{ success: boolean }> {
    if (!value) {
      return of({ success: false });
    }

    const successDialog = this.dialog.open(ChangeEwSuccessPopupComponent, {
      data: {
        AlertForCall: value.AlertForCall,
        MessageText: value.MessageText,
        Number: value.Number,
      },
      panelClass: 'no-padding',
    });
    return successDialog.afterClosed();
  }

  private handleRedirectPopupClose(value: RedirectSuccessPopupData, type: string): Observable<{ success: boolean }> {
    if (!value) {
      return of({ success: false });
    }

    if (!isNumber(value?.Pricing) && !value.ScheduledDeliveryDate && value.success) {
      return of({ success: true });
    }

    const successDialog = this.dialog.open(RedirectSuccessPopupComponent, {
      data: {
        isCreated: value.isCreated,
        Number: value.Number,
        Invoice: this.inputInvoice,
        MessageText: value.MessageText,
        Type: type,
        Pricing: value.Pricing,
        ScheduledDeliveryDate: value.ScheduledDeliveryDate,
      },
      panelClass: 'no-padding',
    });
    return successDialog.afterClosed();
  }

  private handleReturnPopupClose(
    value: BackSuccessPopupData,
    invoice: Invoice,
    type: string,
  ): Observable<{
    success: boolean;
  }> {
    if (!value) {
      return of({ success: false });
    }

    if (!isNumber(value?.Pricing) && !value.ScheduledDeliveryDate && value.success) {
      return of({ success: true });
    }

    const successDialog = this.dialog.open(BackSuccessPopupComponent, {
      data: {
        isCreated: value.isCreated,
        Number: value.Number,
        Pricing: value.Pricing,
        ScheduledDeliveryDate: value.ScheduledDeliveryDate,
        Invoice: invoice,
        Type: type,
        MessageText: value.MessageText,
      },
      panelClass: 'no-padding',
    });
    return successDialog.afterClosed();
  }

  private handleInvoiceMutations(invoice: Invoice): void {
    this.invoice = handleFirstDayStorage(invoice);
    this.invoice = handleReturnCargoDate(this.invoice);
  }

  private isTodayGreaterThanFirstDayStorage(invoice: Invoice): boolean {
    if (!invoice || !invoice.dateFirstDayStorage) {
      return false;
    }

    try {
      const dateFirstDayStorage = new Date(invoice.dateFirstDayStorage);
      return todayEqualOrGreaterThan(dateFirstDayStorage);
    } catch (e) {
      return false;
    }
  }

  private calculateBackMoney(amount: number): string {
    const fee = amount * 0.02 + 20;
    return roundToPrecision(fee, 2);
  }

  private paymentCalc(): void {
    const { backMoney } = this.inputInvoice;

    if (backMoney) {
      this.paymentSum = this.calculateBackMoney(backMoney);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
